// Shared API helper — always sends cookies.
//
// Resolution strategy for the API base URL:
//
// 1. If REACT_APP_BACKEND_URL is set AND its origin matches the page origin,
//    use it (same-origin — no cross-domain cookie issues).
//
// 2. If REACT_APP_BACKEND_URL is set BUT points to a different origin than the
//    current page (e.g., baked-in `app-builder-NNNN.emergent.host` while the
//    user is browsing `https://maxcoach.app`), we silently fall back to a
//    same-origin RELATIVE path. Emergent's K8s ingress routes `/api/*` to the
//    backend on every domain we deploy to, so this Just Works™ — and it
//    eliminates the third-party-cookie auth bug that otherwise breaks Firefox
//    and Safari sign-in.
//
// 3. If REACT_APP_BACKEND_URL is missing entirely, fall back to same-origin.
//
// The result: the app is resilient to a misconfigured production env var.
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function resolveApiBase() {
  // Server-side render / build-time fallback (not used in the browser path).
  if (typeof window === "undefined") {
    return ENV_BACKEND_URL ? `${ENV_BACKEND_URL}/api` : "/api";
  }
  if (!ENV_BACKEND_URL) return "/api";
  try {
    const envOrigin = new URL(ENV_BACKEND_URL).origin;
    const pageOrigin = window.location.origin;
    if (envOrigin === pageOrigin) {
      return `${ENV_BACKEND_URL}/api`;
    }
    // Cross-origin mismatch — fall back to same-origin relative paths.
    // This silently fixes a baked-in wrong REACT_APP_BACKEND_URL.
    if (typeof console !== "undefined" && console.info) {
      console.info(
        "[api] REACT_APP_BACKEND_URL points to a different origin than the page. " +
          "Falling back to same-origin /api requests for cookie safety."
      );
    }
    return "/api";
  } catch {
    return "/api";
  }
}

export const API = resolveApiBase();

export async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (res.status === 401) {
    const err = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }
  return res;
}

export async function apiJSON(path, opts = {}) {
  const res = await apiFetch(path, opts);
  if (!res.ok) {
    let detail = "Request failed";
    try { detail = (await res.json()).detail || detail; } catch {}
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
